import React, { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import styled from "styled-components";

type AnimationDirection = "up" | "down" | "left" | "right" | "fade";

interface LazyLoadedSectionProps {
  children: React.ReactNode;
  threshold?: number;
  visible?: boolean;
  delay?: number;
  direction?: AnimationDirection;
  duration?: number;
  className?: string;
}

interface FadeInSectionProps {
  isVisible: boolean;
  direction: AnimationDirection;
  duration: number;
  delay: number;
}

const getTransform = (direction: AnimationDirection, isVisible: boolean) => {
  if (isVisible) return "translate(0, 0)";

  switch (direction) {
    case "up":
      return "translateY(30px)";
    case "down":
      return "translateY(-30px)";
    case "left":
      return "translateX(30px)";
    case "right":
      return "translateX(-30px)";
    default:
      return "translate(0, 0)";
  }
};

const FadeInSection = styled.div<FadeInSectionProps>`
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: ${({ isVisible, direction }) =>
    getTransform(direction, isVisible)};
  transition: opacity ${({ duration }) => duration}s ease-out,
    transform ${({ duration }) => duration}s ease-out;
  transition-delay: ${({ delay }) => delay}s;
  position: relative;

`;

export function LazyLoadedSection({
  children,
  threshold = 0.1,
  visible: initialVisible = false,
  delay = 0,
  direction = "up",
  duration = 0.6,
  className,
}: LazyLoadedSectionProps) {
  const [isVisible, setIsVisible] = useState<boolean>(initialVisible);
  const { ref, inView } = useInView({
    threshold,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView || initialVisible) {
      setIsVisible(true);
    }
  }, [inView, initialVisible]);

  return (
    <FadeInSection
      ref={ref}
      isVisible={isVisible}
      direction={direction}
      duration={duration}
      delay={delay}
      className={className}
    >
      {children}
    </FadeInSection>
  );
}

export default LazyLoadedSection;
